/**
 * Per-month figures row rendered under a monthly chart, aligned to the
 * chart's month columns -- same layout convention as
 * components/bros/MoMGrowthRow.jsx, generalised to arbitrary labeled
 * money rows instead of a single percentage row. `d[row.key] == null`
 * (genuinely no data that month, not a real zero) renders "-", matching
 * MoMGrowthRow's own null handling.
 */
import { formatCompact } from '../../lib/chartFormatters';

/**
 * @param {object} props
 * @param {Array<{month: string, [key: string]: number|null}>} props.data
 * @param {Array<{key: string, countKey?: string, label: string, className: string}>} props.rows
 *   countKey (optional): a sibling integer field rendered before the
 *   amount as "41 · £93k" -- counts first, since that's what the operator
 *   scans for backlog. Falls back to amount-only when absent (or null on
 *   a genuinely no-data month, matching the amount's own dash rule).
 * @param {string} props.displayCurrency
 */
export function MonthlyFiguresRow({ data, rows, displayCurrency }) {
  if (!data || data.length === 0 || !rows || rows.length === 0) return null;
  return (
    <div className="mt-2 border-t pt-1.5 space-y-1" data-testid="cashflow-figures-row">
      {rows.map((r) => (
        <div className="flex items-center" key={r.key}>
          <span className="text-[10px] text-muted-foreground shrink-0 w-20 text-right pr-2">
            {r.label}
          </span>
          <div className="flex flex-1 min-w-0">
            {data.map((d, i) => {
              const count = r.countKey ? d[r.countKey] : null;
              return (
                <div key={d.month ?? i} className="flex-1 text-center text-[10px] font-mono truncate" title={r.countKey ? `${count ?? '-'} transaction(s)` : undefined}>
                  {d[r.key] == null ? (
                    <span className="text-muted-foreground/50">-</span>
                  ) : (
                    <span className={r.className}>
                      {r.countKey && count != null && (
                        <span className="text-muted-foreground/70 font-normal">{Number(count).toLocaleString('en-GB')} &middot; </span>
                      )}
                      {formatCompact(d[r.key], displayCurrency)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
